"use client";

// CSS
import "@/styles/input.css";

// Components
import TransparentDiv from "@/components/TransparentDiv";

// React
import { useState } from "react";

// Contexts
import { SessionProvider } from "next-auth/react";
import { IntlProvider } from "react-intl";
import PopUpsDisplayContext, { PopUp } from "@/contexts/popUpsDisplay.context";
import AppCurrentLangContext from "@/contexts/appCurrentLang.context";
import TransparentDivContext from "@/contexts/transparentDiv.context";
import AreLangFlagsDisplayedContext from "@/contexts/areFlagsDisplayed.context";
import UserMenuContext from "@/contexts/userMenu.context";
import ResponsiveMenuContext from "@/contexts/responsiveMenu.context";
import SubscriptionContext, {
  SubscriptionContextValue,
} from "@/contexts/subscription.context";

// Modules
import { langInAppDictionnary } from "@/modules/i18n/i18n.consts";

// Types
import { AvailableLanguageOnApp } from "@/modules/i18n/types/i18n.types";
import ResponsiveMenuContainer from "@/components/Menu/ResponsiveMenu/ResponsiveMenuContainer";
import NavBar from "@/components/Menu/Navbar/NavBar";
import Footer from "@/components/Footer/Footer";
import Popup from "@/components/Popups/PopupContainer";
import { useBootLanguageApp } from "@/modules/i18n/i18n.hooks";
import { useGraphicEffects } from "@/modules/visual/graphicEffects";

// top tree level module
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BubbleChatWrapper from "@/components/Home/bubbleChatWrapper/bubbleChatWrapper";

type ProvidersProps = {
  langHeaders: AvailableLanguageOnApp;
  children: React.ReactNode;
};

export function Providers({ langHeaders, children }: ProvidersProps) {
  const [isTransparentDivDisplayed, setIsTransparentDivDisplayed] =
    useState(false);
  const [isUserMenuDisplayed, setIsUserMenuDisplayed] = useState(false);
  const [popUpsDisplayed, setPopUpsDisplayed] = useState<PopUp[]>([]);
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionContextValue>({
      subscriptionData: {
        justSubscribedSuccessfully: false,
        selectedFormulaId: null,
        subscriptionsFormulas: [],
      },
    });

  const [isResponsiveMenuDisplayed, setIsResponsiveMenuDisplayed] =
    useState(false);

  const [areFlagsDisplayed, setAreFlagsDisplayed] = useState(false);

  // Hooks
  const { appCurrentLang, handleSetContextCurrentLang } = useBootLanguageApp(
    langInAppDictionnary,
    langHeaders
  );

  // Context Definitions
  const contextCurrentLang = {
    appCurrentLang,
    setAppCurrentLang: handleSetContextCurrentLang,
  };
  const contextTransparentDiv = {
    isTransparentDivDisplayed,
    setIsTransparentDivDisplayed,
  };

  const contextFlagsDisplayed = {
    areFlagsDisplayed,
    setAreFlagsDisplayed,
  };
  const contextUserMenuDisplayed = {
    isUserMenuDisplayed,
    setIsUserMenuDisplayed,
  };
  const contextResponsiveMenuMenuDisplayed = {
    isResponsiveMenuDisplayed,
    setIsResponsiveMenuDisplayed,
  };
  const contextSubscription = {
    subscriptionData: subscriptionData.subscriptionData,
    setSubscriptionData,
  };

  const contextPopUpsDisplayed = {
    popUpsDisplayed,
    setPopUpsDisplayed,
    addPopUp: (popUp: PopUp) => setPopUpsDisplayed([...popUpsDisplayed, popUp]),
    removeSpecificPopUp: (index: number) => {
      const arrayPopUpCopy = [...popUpsDisplayed];
      arrayPopUpCopy.splice(index, 1);
      setPopUpsDisplayed(arrayPopUpCopy);
    },
  };

  // Ajout des animations en fondu dans l'ensemble de l'app
  useGraphicEffects();

  return (
    <SessionProvider>
      <UserMenuContext.Provider value={contextUserMenuDisplayed}>
        <SubscriptionContext.Provider value={contextSubscription}>
          <ResponsiveMenuContext.Provider
            value={contextResponsiveMenuMenuDisplayed}
          >
            <AppCurrentLangContext.Provider value={contextCurrentLang}>
              <AreLangFlagsDisplayedContext.Provider
                value={contextFlagsDisplayed}
              >
                <TransparentDivContext.Provider value={contextTransparentDiv}>
                  <PopUpsDisplayContext.Provider value={contextPopUpsDisplayed}>
                    <IntlProvider
                      locale={appCurrentLang?.locale}
                      messages={appCurrentLang?.translatedText}
                    >
                      {isTransparentDivDisplayed && <TransparentDiv />}
                      {Array.isArray(popUpsDisplayed) &&
                        popUpsDisplayed.map((popUp, index) => (
                          <Popup
                            index={index}
                            key={index}
                            CompoToRender={popUp.CompoToRender}
                            {...popUp}
                          />
                        ))}
                      <ToastContainer
                        position="bottom-right"
                        autoClose={5000}
                        pauseOnHover
                        theme="colored"
                      />
                      <ResponsiveMenuContainer />
                      <NavBar />
                      {children}
                      <BubbleChatWrapper />
                      <Footer />
                    </IntlProvider>
                  </PopUpsDisplayContext.Provider>
                </TransparentDivContext.Provider>
              </AreLangFlagsDisplayedContext.Provider>
            </AppCurrentLangContext.Provider>
          </ResponsiveMenuContext.Provider>
        </SubscriptionContext.Provider>
      </UserMenuContext.Provider>
    </SessionProvider>
  );
}
